import React, { useEffect, useState } from "react";
import {
  Box,
  ButtonBase,
  Typography,
  Divider,
  IconButton,
  Stack,
  Popover,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "hooks/useAuth";
import logo from "../../assets/images/TC_Logo1.png";
import { useSnackbar } from "notistack";
import { ROUTES } from "utils/constants";
import { getRequest } from "network";
import { PROFILE_SUMMARY } from "store/apis";
import DeleteAccountDialog from "components/DeleteAccountDialog"
import Reassessment from "components/Reassessment";
import LogoutConfirmationModal from "components/LogoutConfirmationModal";
import { fetchPromptsSummaryStart } from "store/slices/promptSlice";
import { logoutStart } from "store/slices/authSlice";
import { LAST_PROMPT_EVENT, readLastPrompt } from "utils/lastPrompt.utils";
import {
  Bookmark,
  ClipboardCheck,
  ClipboardPlus,
  Fingerprint,
  Lock,
  LogIn,
  LogOut,
  Menu,
  MessageCircleQuestion,
  PanelLeftClose,
  Trash,
  User,
  Users,
} from "lucide-react";

const drawerWidth = 280;

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: open ? drawerWidth : 70,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": {
    width: open ? drawerWidth : 70,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
  },
}));

const NAV_ACCENT = "#8B5CF6";
const NAV_ACTIVE_INK = "#6D3FE0";
const NAV_ACTIVE_BG = "#F1ECFE";

export default function AppHeader({ open, onToggle }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const { userResult } = useSelector((state) => state.assessment);
  const { counts } = useSelector((state) => state.prompt.promptsSummary);

  const hasCompletedAssessment = !!userResult?.projectivePersonality;

  const [lastPrompt, setLastPrompt] = useState(readLastPrompt);
  const [anchorEl, setAnchorEl] = useState(null);
  const [reassesmentDialog, setReassesmentDialog] = useState(false)
  const [deleteAccountDialog, setDeleteAccountDialog] = useState(false)
  const [logoutConfirmDialog, setLogoutConfirmDialog] = useState(false)
  const [rejectionShown, setRejectionShown] = useState(false);
  useEffect(() => {
    if (user) dispatch(fetchPromptsSummaryStart());
  }, [dispatch, user]);

  // Surface reassessment rejection, if any
  useEffect(() => {
    if (!user || rejectionShown) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getRequest(PROFILE_SUMMARY);
        if (!cancelled && res?.reassessment && res.reassessment.accepted === false) {
          enqueueSnackbar(res.reassessment.comment || "Your reassessment was rejected.", {
            variant: "warning",
            anchorOrigin: { vertical: isMobile ? 'bottom' : 'top', horizontal: isMobile ? 'center' : 'right' },
          });
          setRejectionShown(true);
        }
      } catch (err) {
        // silent fail
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, enqueueSnackbar, isMobile, rejectionShown]);

  // Keep the "Continue" hint in sync with the last question opened
  useEffect(() => {
    const sync = () => setLastPrompt(readLastPrompt());
    window.addEventListener(LAST_PROMPT_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(LAST_PROMPT_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const path = location.pathname;
  const navItems = [
    {
      key: "questions",
      label: "Questions",
      hint: lastPrompt?.title ? `Continue: ${lastPrompt.title}` : "Read answers from your leaders",
      icon: MessageCircleQuestion,
      to: lastPrompt?.id ? `/app/prompts/${lastPrompt.id}` : ROUTES.APP_PROMPTS,
      active: path.startsWith(ROUTES.APP_PROMPTS),
      tour: "sidebar-questions",
    },
    {
      key: "leaders",
      label: "Leaders",
      icon: Users,
      to: ROUTES.APP_LEADERS,
      active: path.startsWith(ROUTES.APP_LEADERS),
      tour: "sidebar-leaders",
    },
    {
      key: "library",
      label: "Library",
      icon: Bookmark,
      to: `${ROUTES.APP_LIBRARY}?tab=saved`,
      active: path === ROUTES.APP_LIBRARY,
      count: counts?.favourites,
      requiresUser: true,
      tour: "sidebar-favourites",
    },
    user && hasCompletedAssessment
      ? {
          key: "blueprint",
          label: "Your Blueprint",
          icon: Fingerprint,
          to: ROUTES.APP_PERSONALITY_DASHBOARD,
          active: path === ROUTES.APP_PERSONALITY_DASHBOARD,
          tour: "sidebar-personality-dashboard",
        }
      : user
        ? {
            key: "assessment",
            label: "Take the assessment",
            hint: "Personalise every answer to you",
            icon: ClipboardCheck,
            to: ROUTES.APP_ASSESSMENT,
            active: path === ROUTES.APP_ASSESSMENT,
            emphasis: true,
            tour: "sidebar-personality-dashboard",
          }
        : null,
  ].filter(Boolean);

  const goTo = (item) => {
    if (item.requiresUser && !user) {
      enqueueSnackbar("Log in to use your Library.", {
        variant: "warning",
        anchorOrigin: { vertical: isMobile ? 'bottom' : 'top', horizontal: isMobile ? 'center' : 'right' },
      });
      return;
    }
    if (isMobile || isTablet) onToggle();
    navigate(item.to);
  };

  const logoutListener = () => {
    dispatch(logoutStart({
      callback: (err, data) => {
        if (!err) {
          logout(data);
          navigate(ROUTES.ROOT);
        }
      },
    }));
  };

  const ProfileMenuItem = ({ icon, label, onClick, color = "inherit", danger = false }) => (
    <Stack
      direction="row"
      alignItems="center"
      onClick={() => {
        setAnchorEl(null);
        onClick?.();
      }}
      sx={{
        px: 2,
        py: 1.25,
        cursor: "pointer",
        borderRadius: 1.5,
        mx: 1,
        mb: 0.5,
        color: danger ? "#EF4444" : color,
        transition: "all 0.15s ease",
        "&:hover": {
          bgcolor: danger ? "#FEE2E2" : "rgba(0,0,0,0.04)",
        },
      }}
    >
      {icon}
      <Typography sx={{ fontSize: "14px", ml: 1.5, fontWeight: 450 }}>{label}</Typography>
    </Stack>
  );

  const profileBox = () => (
    <Box sx={{ py: 1 }}>
      {user ? (
        <>
          {/* User Info Header */}
          <Box sx={{ px: 2, py: 1.5, mb: 1, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 600 }} noWrap>
              {user?.name || "User"}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "text.secondary" }} noWrap>
              {user?.email}
            </Typography>
          </Box>

         <ProfileMenuItem
          icon={<User size={18} />}
          label="View Profile"
          onClick={() => {
          navigate(ROUTES.PROFILE);
          if (isMobile || isTablet) {
          onToggle();
          }
          }}
      />
         {user && hasCompletedAssessment && (
            <ProfileMenuItem
              icon={<ClipboardPlus size={18} />}
              label="Retake Assessment"
              onClick={() => setReassesmentDialog(true)}
            />
          )}
          <Divider sx={{ my: 1 }} />
          <ProfileMenuItem
            icon={<Trash size={18} />}
            label="Delete Account"
            onClick={() => setDeleteAccountDialog(true)}
            danger
          />
          <ProfileMenuItem
            icon={<LogOut size={18} />}
            label="Logout"
            onClick={() => setLogoutConfirmDialog(true)}
            danger
          />
        </>
      ) : (
        <ProfileMenuItem
          icon={<LogIn size={18} />}
          label="Login"
          onClick={() => navigate(ROUTES.LOGIN)}
          color="#3B82F6"
        />
      )}
    </Box>
  );


  const drawerContent = () => (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: open ? "space-between" : "center", px: '1rem', py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
        {open && (
          <Stack direction="row" alignItems="center">
            <img src={logo} alt="Logo" style={{ width: "1.6rem", height: "1.6rem", marginRight: "5px", cursor: 'pointer' }} onClick={() => navigate(ROUTES.ROOT)} />
           
            <Box
            sx={{ display: "flex", alignItems: "flex-start", cursor: "pointer" }}
            onClick={() => navigate(ROUTES.ROOT)}
            >
  
          <Typography
          variant="h6"
          sx={{
          fontSize: "18px",
          fontWeight: 600,
          lineHeight: 1.2,
             }}
          >
          Theecastable
          </Typography>

  
          <Typography
          sx={{
          ml: 0.5,
          fontSize: "0.5rem",
          fontWeight: 700,
          px: 0.6,
          py: "1px",
          borderRadius: "4px",
          backgroundColor: "#EEF2FF",
          color: "#030211",
          position: "relative",
          top: "-6px",
          lineHeight: 1,
          }}
         >
           BETA
          </Typography>
          </Box>

          </Stack>
        )}
        <Tooltip title={open ? "Collapse" : "Expand"} arrow placement="right">
          <IconButton
            data-tour="sidebar-toggle"
            onClick={onToggle}
            size="small"
            aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
            sx={{
              width: 30,
              height: 30,
              p: 0,
              borderRadius: "10px",
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "#fff",
              boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
              "&:hover": {
                bgcolor: "grey.100",
              },
            }}
          >
            {open ? <PanelLeftClose size={18} /> : <Menu size={18} />}
          </IconButton>
        </Tooltip>
      </Box>


      {/* Navigation */}
      <Box component="nav" aria-label="Main" data-tour="sidebar" sx={{ px: 1.5, py: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
        {navItems.map((item) => {
          const locked = item.requiresUser && !user;
          const Icon = item.icon;
          const row = (
            <ButtonBase
              key={item.key}
              data-tour={item.tour}
              onClick={() => goTo(item)}
              aria-current={item.active ? "page" : undefined}
              aria-label={open ? undefined : item.label}
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: open ? "flex-start" : "center",
                gap: 1.5,
                minHeight: 44,
                px: open ? 1.25 : 0,
                py: item.hint && open ? 0.75 : 0,
                borderRadius: "10px",
                textAlign: "left",
                color: item.active ? NAV_ACTIVE_INK : item.emphasis ? NAV_ACTIVE_INK : "#3F3B4D",
                bgcolor: item.active ? NAV_ACTIVE_BG : "transparent",
                opacity: locked ? 0.6 : 1,
                transition: "background-color 0.2s ease, color 0.2s ease",
                "&:hover": { bgcolor: item.active ? NAV_ACTIVE_BG : "rgba(23, 21, 31, 0.04)" },
                "&.Mui-focusVisible": { outline: `2px solid ${NAV_ACCENT}`, outlineOffset: 1 },
              }}
            >
              <Box sx={{ display: "flex", flexShrink: 0, color: item.active || item.emphasis ? NAV_ACCENT : "#6E6A7C" }}>
                <Icon size={20} strokeWidth={1.75} />
              </Box>
              {open && (
                <>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: item.active ? 600 : 500, lineHeight: 1.3, color: "inherit" }}>
                      {item.label}
                    </Typography>
                    {item.hint && (
                      <Typography sx={{ fontSize: "12px", color: "text.secondary", lineHeight: 1.35 }} noWrap>
                        {item.hint}
                      </Typography>
                    )}
                  </Box>
                  {locked ? (
                    <Lock size={14} color="#9CA3AF" />
                  ) : typeof item.count === "number" && item.count > 0 ? (
                    <Typography sx={{ fontSize: "12px", color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                      {item.count}
                    </Typography>
                  ) : null}
                </>
              )}
            </ButtonBase>
          );
          return open ? row : (
            <Tooltip key={item.key} title={item.label} placement="right" arrow>
              {row}
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={{ flexGrow: 1 }} />

      {/* User Profile Section */}
      {(isMobile || isTablet) ? profileBox() : (
        <Box data-tour="sidebar-profile" sx={{ p: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
          <Stack
            direction="row"
            alignItems="center"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              cursor: "pointer",
              p: 1,
              borderRadius: 2,
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: "rgba(0,0,0,0.04)",
              },
            }}
          >
            {/* Avatar */}
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: user
                  ? "linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)"
                  : "#9CA3AF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontWeight: 600,
                fontSize: "14px",
                flexShrink: 0,
              }}
            >
              {user ? user.name?.charAt(0)?.toUpperCase() || "U" : "G"}
            </Box>

            {open && (
              <Box sx={{ ml: 1.5, flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 500 }} noWrap>
                  {user ? user.name : "Guest"}
                </Typography>
                {user && (
                  <Typography sx={{ fontSize: "11px", color: "text.secondary" }} noWrap>
                    {user.email}
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        </Box>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "12px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            width: isMobile ? "90vw" : "260px",
          },
        }}
      >
        {profileBox()}
      </Popover>
      <Reassessment open={reassesmentDialog} handleClose={() => setReassesmentDialog(false)} />
      <DeleteAccountDialog open={deleteAccountDialog} handleClose={() => setDeleteAccountDialog(false)} />
      <LogoutConfirmationModal
        open={logoutConfirmDialog}
        onClose={() => setLogoutConfirmDialog(false)}
        onConfirm={logoutListener}
      />
    </>
  );

  return (
    (isMobile || isTablet)
      ? <>
        <Box sx={{ display: { xs: 'flex', md: 'none' }, direction: 'row', justifyContent: 'space-between', my: '0.5rem', px: '0.5rem', alignItems: 'center', }}>
          <Stack direction="row" alignItems="center">
            <img src={logo} alt="Logo" style={{ width: "1.8rem", height: "1.8rem", marginRight: "8px", cursor: 'pointer' }} onClick={() => navigate(ROUTES.ROOT)} />
            <Box
                sx={{ display: "flex", alignItems: "flex-start", cursor: "pointer" }}
                onClick={() => navigate(ROUTES.ROOT)}
            >
            <Typography
            variant="h6"
            sx={{
            fontSize: "20px",
            fontWeight: 600,
            lineHeight: 1.2,
            }}
            >
            Theecastable
          </Typography>

          <Typography
          sx={{
          ml: 0.5,
          fontSize: "0.5rem",
          fontWeight: 700,
          px: 0.6,
          py: "1px",
          borderRadius: "4px",
          backgroundColor: "#EEF2FF",
          color: "#000000",
          position: "relative",
          top: "-6px",
          lineHeight: 1,
        }}
      >
        BETA
        </Typography>
        </Box>

          </Stack>
          <Tooltip title={open ? "Collapse" : "Expand"} arrow placement="bottom">
            <IconButton data-tour="sidebar-toggle" onClick={onToggle} size="small" aria-label={open ? "Collapse sidebar" : "Expand sidebar"}>
              <Menu />
            </IconButton>
          </Tooltip>
        </Box>
        <MuiDrawer anchor="right" open={open} onClose={onToggle} variant="temporary" ModalProps={{ keepMounted: true }} sx={{ '& .MuiDrawer-paper': { width: '100%', maxWidth: '100%' } }}>{drawerContent()}</MuiDrawer>
      </>
      : <Drawer variant="permanent" open={open} data-tour="sidebar-drawer">{drawerContent()}</Drawer>
  );
}
